<template>
  <div class="card chat-list">
    <div class="card-header">
      <span class="card-title">
        <i class="fas fa-comment-dots"></i> Chats
      </span>
      <div class="card-header-actions">
        <button
          class="card-header-action btn btn-primary dropdown-toggle px-2  "
          id="dropdownAddRoom"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          Add
        </button>
        <div class="dropdown-menu" aria-labelledby="dropdownAddRoom">
          <a href="#" class="dropdown-item add-room-btn" @click.prevent="createNewConversation">
            <i class="fas fa-users"></i> Add New Chat Room
          </a>
        </div>
      </div>
    </div>

    <div class="card-body overflow-auto">
      <div v-show="showChatForm || showGroupForm" class="border-bottom pt-4 px-4 pb-2">
        <h5>{{ isEditMode ? 'Update Group Info' : 'Create New Chat Room' }}</h5>

        <form @submit.prevent="saveRoomDetails" :id="isEditMode ? 'form-edit-room' : 'form-add-room-float'">
          <div v-if="isEditMode && isLoadingMembers" class="loading-spinner">
            Loading members...
          </div>
          <div v-else>
            <div class="form-group">
              <label>Title</label>
              <input type="text" v-model="title" name="title" class="form-control" required>
            </div>
            <div class="form-group">
              <label>Select Users</label>
              <v-select v-model="selectedUsers" class="select-users" multiple label="name" :options="userOptions" />
            </div>
            <div class="form-group text-center">
              <button type="submit" class="btn btn-sm btn-primary" :disabled="isSavingRoom">
                <span v-if="isSavingRoom">
                  {{ isEditMode ? 'Updating room...' : 'Creating room...' }}
                </span>
                <span v-else>
                  <i :class="isEditMode ? 'fas fa-pen-nib' : 'far fa-comments'"></i>
                  {{ isEditMode ? 'Update' : 'Start Chat' }}
                </span>
              </button>
              <button type="button" class="btn btn-sm btn-danger">
                {{ isEditMode ? 'Close' : 'Cancel' }}
              </button>
            </div>
          </div>
        </form>
      </div>

      <template v-if="!isLoading && chatConversations.length > 0">
        <div v-for="room in chatConversations" :key="room.id" class="conversation-list-item">
          <template v-if="room.type === 'single'">
            <a href="#" class="list-group-item list-group-item-action open-floating-room mb-2" @click.prevent="openChatConversation(room)">
              <div class="media">
                <img :src="room.user_image" :alt="room.user_name" class="user-thumbnail" loading="lazy">
                <div class="media-body ml-2">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <div class="mb-0">
                      <span>{{ room.user_name }}</span>
                      <span class="unread-badge" :data-room-id="room.id">
                        <span v-if="room.pivot.unread > 0" class="badge badge-danger badge-pill unread-badge">
                          {{ room.pivot.unread }}
                        </span>
                      </span>
                    </div>
                    <small class="font-weight-bold">
                      {{ formatMessageTime(room.last_update) }}
                    </small>
                  </div>
                  <p class="font-italic mb-0 text-small">
                    <span class="chat-status" :class="getStatusClass(room.user_status, room.user_status_label)" />
                    <span class="chat-status-text">
                      {{ formatUserStatus(room) }}
                    </span>
                  </p>
                </div>
              </div>
            </a>
          </template>
          <template v-else>
            <a href="#" class="list-group-item list-group-item-action mb-2" @click.prevent="openChatConversation(room)">
              <div class="media">
                <div class="media-body ml-2">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <h6 class="mb-0">{{ room.title }}</h6>
                    <small class="font-weight-bold">
                      {{ formatMessageTime(room.last_update) }}
                    </small>
                  </div>
                  <p class="font-italic mb-0 text-small">
                    <span>{{ room.users }}</span>
                  </p>
                </div>
              </div>
            </a>
          </template>
        </div>
      </template>

      <template v-if="isLoading || chatConversations.length === 0">
        <p class="text-center mt-4">
          {{ isLoading ? 'Loading your chat conversations..' : 'No chats found.' }}
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import Noty from "noty";
import { mapMutations } from "vuex";
import { eventBus, formatDateTime } from "../../../../utils/helpers";
import { createChatRoom, getBranches, getChatConversationList, getGroupMember, updateChatRoom } from "../../../../services/api/uni-chat";

import "vue-select/dist/vue-select.css";

export default {
  name: 'ConversationList',
  components: {
    'v-select': vSelect
  },
  data() {
    return {
      isLoading: false,
      isLoadingMembers: false,
      isSavingRoom: false,
      isEditMode: false,
      showChatForm: false,
      showGroupForm: false,
      showList: true,
      branches: [],
      chatConversations: [],
      title: '',
      selectedUsers: [],
      selectedConversation: [],
      roomIndexMap: {}
    }
  },
  mounted() {
    this.getAllBranches();
    this.getChatConversations();
    eventBus.$on('update-unread-room', this.updateUnreadMessages);
    eventBus.$on('toggle-chat-form', this.toggleChatForm);
    eventBus.$on('delete-chat-room', this.getChatConversations);

    eventBus.$on('update-chat-room', (room) => {
      const index = this.chatConversations.findIndex(chat => chat.id === room.id);

      if (index !== -1) {
        this.$set(this.chatConversations, index, {
          ...this.chatConversations[index],
          title: room.title,
          users: room.users
        });
      }
    });
  },
  computed: {
    userOptions() {
      const options = [];
      this.branches.forEach(branch => {
        branch.users.forEach(user => {
          options.push({
            id: user.id,
            name: user.name
          });
        });
      });
      return options;
    }
  },
  methods: {
    ...mapMutations("chat", ["SET_ACTIVE_CONVERSATION"]),

    formatMessageTime(created_at) {
      return formatDateTime(created_at);
    },
    formatUserStatus(room) {
      const lastOnline = room.last_online ? this.formatMessageTime(room.last_update) : 'Never';

      if (room.user_status === 'busy') {
        return `${room.user_status_label} | Last Online: ${lastOnline}`;
      } else {
        const formattedStatus = room.user_status.replace(/^\w/, c => c.toUpperCase());
        return `${formattedStatus} | Last Online: ${lastOnline}`;
      }
    },
    getStatusClass(status, label) {
      const statusClasses = {
        'online': 'is-active',
        'visit': 'is-visit',
        'training': 'is-training',
        'lunch': 'is-lunch',
        'dnd': 'is-dnd',
        'call': 'is-call',
        'video-call': 'is-video-call',
      };

      const formattedLabel = label ? label.toLowerCase().replace(/\s+/g, '-') : null;
      const selectedStatus = status === 'busy' ? statusClasses[formattedLabel] : statusClasses[status];

      return selectedStatus || 'is-inactive';
    },
    async createChatConversation() {
      try {
        this.isSavingRoom = true;

        const userIds = this.selectedUsers.map(user => user.id);
        const payload = {
          type: 'group',
          title: this.title,
          users: userIds
        };

        const response = await createChatRoom(payload);

        if (response.status === 200) {
          this.closeForm();

          new Noty({
            type: 'success',
            text: 'Chat conversation created successfully!',
            timeout: 3000
          }).show();

          await this.getAllBranches();
          await this.getChatConversations(response.data.id);
        }
      } catch (error) {
        console.error("Error creating chat room:", error);
      } finally {
        this.isSavingRoom = false;
      }
    },
    async getAllBranches() {
      try {
        this.isLoading = true;

        const response = await getBranches();
        this.branches = response.data.data;
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async getChatConversations(room_id = null) {
      try {
        this.isLoading = true;

        const response = await getChatConversationList();
        this.chatConversations = response.data;

        this.chatConversations.forEach((room, index) => {
          this.roomIndexMap[room.id] = index;
        });

        if (room_id) {
          const room = this.chatConversations.find(room => room.id === room_id);
          room && this.openChatConversation(room);
        }
      } catch (error) {
        console.error('Error fetching chat conversations:', error);
      } finally {
        this.isLoading = false;
      }
    },
    openChatConversation(room) {
      try {
        eventBus.$emit('close-chat-room', room.id);

        this.SET_ACTIVE_CONVERSATION(room.id);
        this.$emit('selected', room);
      } catch (error) {
        console.error('Error opening chat conversation:', error);
      }
    },
    updateUnreadMessages(room_id, unread) {
      const roomIndex = this.roomIndexMap[room_id];

      if (roomIndex !== undefined) {
        this.$set(this.chatConversations[roomIndex].pivot, 'unread', unread);
      }
    },
    async toggleChatForm(room) {
      this.isEditMode = true;
      this.showGroupForm = !this.showGroupForm;
      this.selectedConversation = room;
      this.title = room.title;

      await this.getGroupMembers(room);
    },
    async getGroupMembers(room) {
      try {
        const response = await getGroupMember(room.id);

        if (response.status === 200) {
          this.selectedUsers = this.userOptions.filter(user => response.data.includes(user.id));
        }
      } catch (error) {
        console.error('Error fetching group members:', error);
      }
    },
    async updateGroupInfo() {
      try {
        this.isSavingRoom = true;

        const userIds = this.selectedUsers.map(user => user.id);
        const payload = {
          type: 'group',
          title: this.title,
          users: userIds,
          room_id: this.selectedConversation.id
        };

        const response = await updateChatRoom(payload);

        if (response.status === 200) {
          this.closeForm();

          new Noty({
            type: 'success',
            text: response.data.message,
            timeout: 3000
          }).show();

          await this.getAllBranches();
          await this.getChatConversations(this.selectedConversation.id);
        }
      } catch (error) {
        console.error("Error updating chat room:", error);
      } finally {
        this.isSavingRoom = false;
      }
    },
    saveRoomDetails() {
      this.isEditMode ? this.updateGroupInfo() : this.createChatConversation();
    },
    createNewConversation() {
      this.isEditMode = false;
      this.showChatForm = true;
      this.title = '';
      this.selectedUsers = [];
    },
    closeForm() {
      this.showChatForm = false;
      this.showGroupForm = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-list {
  border: none;
  border-radius: 3px 0 0 0;
  margin-bottom: 0;
  height: calc(100vh - 180px);

  .card-header {
    background: #ced3de;
    border-radius: 3px 0 0 0;
  }

  .card-body {
    .user-thumbnail {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      object-fit: fill;
    }

    .list-group-item {
      &:hover {
        background-color: #009f91;
        color: white;
      }
    }
  }
}
</style>